import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Accordion, AccordionSection } from 'ethos-design-system';

import { AdditionalDocuments } from '@/features/tasks/types/additionalDocuments';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { Alert, Box, Button, Divider, Drawer, Typography } from '@mui/material';
import { Dictionary } from '@reduxjs/toolkit';

import {
  canCreatePlanFTask,
  canCreateSpecialAuthorizationTask,
  canOrderAdditionalDocuments,
  canOrderInfoFromCustomer,
  isAdmin,
} from '../../../../common/actionHelper';
import { IconLink } from '../../../../common/components/IconLink';
import { NoraButton } from '../../../../common/components/v2';
import {
  Reason,
  ReasonsEnum,
  ReasonsTransform,
} from '../../../../common/constants/reasons';
import { Resource } from '../../../../common/constants/resource';
import { NoraPermissionV2 } from '../../../../common/constants/roles';
import { FeatureFlagsContext } from '../../../../common/featureFlagsWrapper';
import { useAccess } from '../../../../common/hooks/useAccess';
import { EvidenceOrderProfile } from '../../../../common/productConfig/productConfig';
import { ProductFactory } from '../../../../common/productConfig/productFactory';
import { ITask } from '../../../tasks/interfaces/ITask';
import { AdditionalCustomerInfo } from '../../../tasks/types/additionalCustomerInfo';
import { Aps } from '../../../tasks/types/aps';
import { AutoMedicalClaims } from '../../../tasks/types/autoMedicalClaims';
import { CriminalRecords } from '../../../tasks/types/criminalRecords';
import { Dispute } from '../../../tasks/types/dispute';
import { EHR } from '../../../tasks/types/ehr';
import { Eti } from '../../../tasks/types/eti';
import { InsuranceLabs } from '../../../tasks/types/insuranceLabs';
import { LabPiqture } from '../../../tasks/types/labPiqture';
import { MedExam } from '../../../tasks/types/medExam';
import { MedicalClaims } from '../../../tasks/types/medicalClaims';
import { MibCodeDetails } from '../../../tasks/types/mibCodeDetails';
import { PlanF } from '../../../tasks/types/planF';
import { ScriptCheck } from '../../../tasks/types/scriptCheck';
import { SpecialAuthorization } from '../../../tasks/types/specialAuthorization';
import { TaskTypes } from '../../../tasks/types/taskTypes';
import { TRLOrder } from '../../../tasks/types/TRLOrder';
import { IExpandedState } from '../../interfaces/IExpandedState';
import styles from '../styles.module.scss';
import { APSLayout } from './aps/APSLayout';
import { DisputeLayout } from './dispute/DisputeLayout';
import { EHRLayout } from './ehr/EHRLayout';
import { ETILayout } from './eti/ETILayout';
import { InsuranceLabsLayout } from './insuranceLabs/InsuranceLabsLayout';
import { MedExamLayout } from './medExam/MedExamLayout';
import { MibCodeDetailsLayout } from './mibCodeDetails/MibCodeDetailsLayout';
import { SimpleLayout } from './simple/SimpleLayout';

const AddToggle = () => {
  const iconContainerClass = `${styles.IconLinkContainer} ${styles.BlueIconLinkContainer}`;
  const textClass = `${styles.LinkRight} ${styles.RightMargin}`;

  return (
    <IconLink
      icon={faPlus}
      copy="Add to order"
      iconClassName={styles.Icon}
      textClassName={textClass}
      iconContainerClassName={iconContainerClass}
    />
  );
};

const DeleteToggle = () => {
  const iconContainerClass = `${styles.IconLinkContainer} ${styles.RedIconLinkContainer}`;
  const textClass = `${styles.LinkRight} ${styles.RightMargin}`;

  return (
    <IconLink
      icon={faTrash}
      copy="Remove"
      iconClassName={styles.Icon}
      textClassName={textClass}
      iconContainerClassName={iconContainerClass + ' redlink'}
    />
  );
};

const REVIEW_BUTTON_NAME = 'evidence-drawer-review-button';

export const OrderEvidence = ({
  product,
  carrier,
  subCategory,
  existingTasks,
  isOpen,
  setIsOpen,
  setIsReview,
  reasons,
  expanded,
  setExpanded,
  setMedicalExam,
  medicalExam,
  setEti,
  eti,
  setAps,
  aps,
  ehr,
  setEhr,
  labPiqture,
  setLabPiqture,
  medicalClaims,
  setMedicalClaims,
  scriptCheck,
  setScriptCheck,
  mibCodeDetails,
  setMibCodeDetails,
  insuranceLabs,
  setInsuranceLabs,
  criminalRecords,
  setCriminalRecords,
  additionalCustomerInfo,
  setAdditionalCustomerInfo,
  specialAuthorization,
  setSpecialAuthorization,
  planF,
  setPlanF,
  autoMedicalClaims,
  setAutoMedicalClaims,
  trlOrder,
  setTRLOrder,
  dispute,
  setDispute,
  additionalDocuments,
  setAdditionalDocuments,
}: {
  product: string;
  carrier: string;
  subCategory: string;
  existingTasks: Dictionary<ITask>;
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  setIsReview: Dispatch<SetStateAction<boolean>>;
  reasons: Reason[];
  expanded: IExpandedState;
  setExpanded: Dispatch<SetStateAction<IExpandedState>>;
  setMedicalExam: Dispatch<SetStateAction<MedExam>>;
  medicalExam: MedExam;
  setEti: Dispatch<SetStateAction<Eti>>;
  eti: Eti;
  setAps: Dispatch<SetStateAction<Aps>>;
  aps: Aps;
  ehr: EHR;
  setEhr: Dispatch<SetStateAction<EHR>>;
  labPiqture: LabPiqture;
  setLabPiqture: Dispatch<SetStateAction<LabPiqture>>;
  medicalClaims: MedicalClaims;
  setMedicalClaims: Dispatch<SetStateAction<MedicalClaims>>;
  scriptCheck: ScriptCheck;
  setScriptCheck: Dispatch<SetStateAction<ScriptCheck>>;
  mibCodeDetails: MibCodeDetails;
  setMibCodeDetails: Dispatch<SetStateAction<MibCodeDetails>>;
  insuranceLabs: InsuranceLabs;
  setInsuranceLabs: Dispatch<SetStateAction<InsuranceLabs>>;
  criminalRecords: CriminalRecords;
  setCriminalRecords: Dispatch<SetStateAction<CriminalRecords>>;
  additionalCustomerInfo: AdditionalCustomerInfo;
  setAdditionalCustomerInfo: Dispatch<SetStateAction<AdditionalCustomerInfo>>;
  specialAuthorization: SpecialAuthorization;
  setSpecialAuthorization: Dispatch<SetStateAction<SpecialAuthorization>>;
  planF: PlanF;
  setPlanF: Dispatch<SetStateAction<PlanF>>;
  autoMedicalClaims: AutoMedicalClaims;
  setAutoMedicalClaims: Dispatch<SetStateAction<AutoMedicalClaims>>;
  trlOrder: TRLOrder;
  setTRLOrder: Dispatch<SetStateAction<TRLOrder>>;
  dispute: Dispute;
  setDispute: Dispatch<SetStateAction<Dispute>>;
  additionalDocuments: AdditionalDocuments;
  setAdditionalDocuments: Dispatch<SetStateAction<AdditionalDocuments>>;
}) => {
  const { flags } = useContext(FeatureFlagsContext);

  const canOrderInfoFromCustomerAccess = useAccess({
    permission: NoraPermissionV2.ORDER_TASK,
    legacyCheck: canOrderInfoFromCustomer,
  });
  const canOrderAdditionalDocumentsAccess = useAccess({
    permission: NoraPermissionV2.ORDER_TASK,
    legacyCheck: canOrderAdditionalDocuments,
  });
  const canCreateSpecialAuthorizationAccess = useAccess({
    permission: NoraPermissionV2.ORDER_TASK,
    legacyCheck: canCreateSpecialAuthorizationTask,
  });
  const canCreatePlanFAccess = useAccess({
    permission: NoraPermissionV2.ORDER_TASK,
    legacyCheck: canCreatePlanFTask,
  });
  // Admin bypass when product does not offer automated criminal records.
  const canOrderCriminalRecordsAsAdmin = useAccess({
    permission: NoraPermissionV2.VIEW_CONTROL_PANEL,
    legacyCheck: isAdmin,
  });
  const onToggle = (id: number) => {
    setExpanded({
      ...expanded,
      [id]: !expanded[id],
    });
  };

  const toggleChild = (isExpanded: boolean) => {
    return (
      <span aria-hidden={true}>
        {isExpanded ? <DeleteToggle /> : <AddToggle />}
      </span>
    );
  };

  const apsReasons: Reason[] = useMemo(() => {
    const list = Array.isArray(reasons) ? reasons : [];
    const ehrInsufficient = ReasonsTransform.getReason(
      ReasonsEnum.EHR_INSUFFICIENT,
    );
    return ehrInsufficient ? [...list, ehrInsufficient] : list;
  }, [reasons]);

  // TODO: refactor how we set and access the expanded state
  const medExamExpanded = expanded[0];
  const etiExpanded = expanded[1];
  const ehrExpanded = expanded[2];
  const apsExpanded = expanded[3];
  const labPiqtureExpanded = expanded[4];
  const autoMedicalClaimsExpanded = expanded[5];
  const trlExpanded = expanded[6];
  const medicalClaimsExpanded = expanded[7];
  const scriptCheckExpanded = expanded[8];
  const insuranceLabsExpanded = expanded[9];
  const mibCodeDetailsExpanded = expanded[10];
  const criminalRecordsExpanded = expanded[11];
  const additionalCustomerInfoExpanded = expanded[12];
  const specialAuthorizationExpanded = expanded[13];
  const planFExpanded = expanded[14];
  const disputeExpanded = expanded[15];
  const additionalDocumentsExpanded = expanded[16];
  const medExamValid = medExamExpanded
    ? (medicalExam.labs ||
        medicalExam.physicalMeasurements ||
        medicalExam.ekg) &&
      medicalExam.reason &&
      medicalExam.reason.length > 0
    : true;

  const nativeEtiValid = eti.reason && eti.reason.length > 0;

  const legacyEtiValid = eti.question && eti.reason && eti.reason.length > 0;

  const etiValid = etiExpanded
    ? flags.uwNativeEtiEnabled
      ? nativeEtiValid
      : legacyEtiValid
    : true;

  const ehrValid = ehrExpanded ? ehr.reason?.length > 0 : true;

  const legacyApsValid = apsExpanded
    ? aps.doctorInfo && aps.reason && aps.reason.length > 0
    : true;

  const nativeApsValid = apsExpanded
    ? aps.reason && aps.reason.length > 0
    : true;

  const apsValid = flags.isApsDoctorInfoEnabled
    ? nativeApsValid
    : legacyApsValid;

  const labPiqtureValid = labPiqtureExpanded
    ? labPiqture.reason?.length > 0
    : true;

  const medicalClaimsValid = medicalClaimsExpanded
    ? medicalClaims.reason?.length > 0
    : true;

  const autoMedicalClaimsValid = autoMedicalClaimsExpanded
    ? autoMedicalClaims.reason?.length > 0
    : true;

  const trlValid = trlExpanded ? trlOrder.reason?.length > 0 : true;

  const scriptCheckValid = scriptCheckExpanded
    ? scriptCheck.reason?.length > 0
    : true;

  const criminalRecordsValid = criminalRecordsExpanded
    ? criminalRecords.reason?.length > 0
    : true;

  const additionalCustomerValid = additionalCustomerInfoExpanded
    ? additionalCustomerInfo.reasonDescription
    : true;

  const specialAuthorizationValid = specialAuthorizationExpanded
    ? specialAuthorization.reasonDescription
    : true;

  const planFValid = planFExpanded ? planF.reasonDescription : true;

  const disputeValid = disputeExpanded ? dispute.reason?.length > 0 : true;

  const mibCodeDetailsValid = mibCodeDetailsExpanded
    ? mibCodeDetails.reason?.length > 0 && !!mibCodeDetails.code
    : true;

  const insuranceLabsValid = insuranceLabsExpanded
    ? insuranceLabs.reason?.length > 0 && !!insuranceLabs.approxDateOfLabs
    : true;

  const additionalDocumentsValid = additionalDocumentsExpanded
    ? additionalDocuments.reasonDescription
    : true;

  const [medExamError, setMedExamError] = useState(false);
  const [etiError, setEtiError] = useState(false);
  const [ehrError, setEhrError] = useState(false);
  const [apsError, setApsError] = useState(false);
  const [labPiqtureError, setLabPiqtureError] = useState(false);
  const [autoMedicalClaimsError, setAutoMedicalClaimsError] = useState(false);
  const [trlError, setTRLError] = useState(false);
  const [medicalClaimsError, setMedicalClaimsError] = useState(false);
  const [scriptCheckError, setScriptCheckError] = useState(false);
  const [criminalRecordsError, setCriminalRecordsError] = useState(false);
  const [mibCodeDetailsError, setMibCodeDetailsError] = useState(false);
  const [insurnaceLabsError, setInsurnaceLabsError] = useState(false);
  const [addtionalCustomerError, setAddtionalCustomerError] = useState(false);
  const [specialAuthorizationError, setSpecialAuthorizationError] =
    useState(false);
  const [planFError, setPlanFError] = useState(false);
  const [disputeError, setDisputeError] = useState(false);
  const [additionalDocumentsError, setAdditionalDocumentsError] =
    useState(false);

  useEffect(() => {
    // If the drawer is closed and then re-opened, the error bar should re-set
    if (!isOpen) {
      setMedExamError(false);
      setEtiError(false);
      setEhrError(false);
      setApsError(false);
      setLabPiqtureError(false);
      setAutoMedicalClaimsError(false);
      setTRLError(false);
      setMedicalClaimsError(false);
      setScriptCheckError(false);
      setCriminalRecordsError(false);
      setMibCodeDetailsError(false);
      setInsurnaceLabsError(false);
      setAddtionalCustomerError(false);
      setAdditionalDocumentsError(false);
    }
    // If any accordion is no longer expanded,
    // the error bar should re-set as this triggers a "removal" of the evidence
    if (!medExamExpanded) {
      setMedExamError(false);
    }
    if (!etiExpanded) {
      setEtiError(false);
    }
    if (!ehrExpanded) {
      setEhrError(false);
    }
    if (!apsExpanded) {
      setApsError(false);
    }
    if (!labPiqtureExpanded) {
      setLabPiqtureError(false);
    }
    if (!autoMedicalClaimsExpanded) {
      setAutoMedicalClaimsError(false);
    }
    if (!trlExpanded) {
      setTRLError(false);
    }
    if (!medicalClaimsExpanded) {
      setMedicalClaimsError(false);
    }
    if (!scriptCheckExpanded) {
      setScriptCheckError(false);
    }
    if (!criminalRecordsExpanded) {
      setCriminalRecordsError(false);
    }
    if (!mibCodeDetailsExpanded) {
      setMibCodeDetailsError(false);
    }
    if (!insuranceLabsExpanded) {
      setInsurnaceLabsError(false);
    }
    if (!additionalCustomerInfoExpanded) {
      setAddtionalCustomerError(false);
    }
    if (!specialAuthorizationExpanded) {
      setSpecialAuthorizationError(false);
    }
    if (!planFExpanded) {
      setPlanFError(false);
    }
    if (!disputeExpanded) {
      setDisputeError(false);
    }
    if (!additionalDocumentsExpanded) {
      setAdditionalDocumentsError(false);
    }
  }, [isOpen, expanded]);

  const taskTypeExist = (taskType: string) => {
    return (
      existingTasks &&
      Object.values(existingTasks).some(task => task?.taskType === taskType)
    );
  };
  const productConfig = ProductFactory.getProductConfig(
    carrier,
    product,
    subCategory,
  );
  const showsInsuredEvidenceOrderNotice =
    !!productConfig &&
    productConfig.getEvidenceOrderProfile() === EvidenceOrderProfile.INSURED;
  return (
    <Drawer
      open={isOpen}
      anchor="right"
      variant="persistent"
      classes={{ paper: styles.Drawer }}
      transitionDuration={{ enter: 0, exit: 0 }}
    >
      <Box mt={6}>
        <Box
          m={3}
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="body1">Order Evidences</Typography>
          <Button onClick={() => setIsOpen(false)}>
            <Typography variant="subtitle2" color="textSecondary">
              Cancel
            </Typography>
          </Button>
        </Box>
        <Divider />
        {showsInsuredEvidenceOrderNotice && (
          <Box mx={3} mt={2}>
            <Alert severity="info">
              Evidence will be ordered for the Insured profile.
            </Alert>
          </Box>
        )}
        <Box className={styles.OrderEvidenceAccordion}>
          <Accordion
            expanded={expanded}
            toggleChildIsTarget={true}
            onToggle={onToggle}
          >
            <AccordionSection
              labelClassName={
                productConfig?.medicalExamAvailable()
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              title="Medical Exams"
              renderToggle={toggleChild}
            >
              <MedExamLayout
                medExam={medicalExam}
                setMedExam={setMedicalExam}
                isExpanded={medExamExpanded}
                reasons={reasons}
                medExamError={medExamError}
              />
            </AccordionSection>
            <AccordionSection
              labelClassName={
                productConfig?.ETIAvailable()
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              title="ETI"
              renderToggle={toggleChild}
            >
              <ETILayout
                eti={eti}
                setEti={setEti}
                isExpanded={etiExpanded}
                reasons={reasons}
                etiError={etiError}
              />
            </AccordionSection>

            <AccordionSection
              title="EHR"
              labelClassName={styles.EvidenceTitle}
              renderToggle={toggleChild}
            >
              <EHRLayout
                data={ehr}
                setData={setEhr}
                isExpanded={ehrExpanded}
                reasons={reasons}
                error={ehrError}
              />
            </AccordionSection>

            <AccordionSection
              labelClassName={styles.EvidenceTitle}
              title="APS"
              renderToggle={toggleChild}
            >
              <APSLayout
                aps={aps}
                setAps={setAps}
                isExpanded={apsExpanded}
                reasons={apsReasons}
                apsError={apsError}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_LAB_PIQTURE}
              labelClassName={styles.EvidenceTitle}
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={labPiqture}
                setData={setLabPiqture}
                isExpanded={labPiqtureExpanded}
                reasons={reasons}
                error={labPiqtureError}
                description={Resource.TASK_DESCRIPTION_LAB_PIQTURE}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_AUTO_MEDICAL_CLAIMS}
              key="TASK_TITLE_AUTO_MEDICAL_CLAIMS"
              labelClassName={
                !taskTypeExist(TaskTypes.AUTO_MEDICAL_CLAIMS) &&
                productConfig?.medicalClaimAvailable()
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={autoMedicalClaims}
                setData={setAutoMedicalClaims}
                isExpanded={autoMedicalClaimsExpanded}
                reasons={reasons}
                error={autoMedicalClaimsError}
                description={Resource.TASK_DESCRIPTION_AUTO_MEDICAL_CLAIMS}
              />
            </AccordionSection>
            <AccordionSection
              title={Resource.TASK_TITLE_TRL}
              key="TASK_TITLE_TRL"
              labelClassName={
                !taskTypeExist(TaskTypes.TRL) && productConfig?.TRLAvailable()
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={trlOrder}
                setData={setTRLOrder}
                isExpanded={trlExpanded}
                reasons={reasons}
                error={trlError}
                description={Resource.TASK_DESCRIPTION_TRL}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_MEDICAL_CLAIMS}
              key="TASK_TITLE_MEDICAL_CLAIMS"
              labelClassName={
                productConfig?.medicalClaimAvailable()
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={medicalClaims}
                setData={setMedicalClaims}
                isExpanded={medicalClaimsExpanded}
                reasons={reasons}
                error={medicalClaimsError}
                description={Resource.TASK_DESCRIPTION_MEDICAL_CLAIMS}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_SCRIPT_CHECK}
              key="TASK_TITLE_SCRIPT_CHECK"
              labelClassName={
                productConfig?.scriptCheckAvailable()
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={scriptCheck}
                setData={setScriptCheck}
                isExpanded={scriptCheckExpanded}
                reasons={reasons}
                error={scriptCheckError}
                description={Resource.TASK_DESCRIPTION_SCRIPT_CHECK}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_INSURANCE_LABS}
              key="TASK_TITLE_INSURANCE_LABS"
              labelClassName={styles.EvidenceTitle}
              renderToggle={toggleChild}
            >
              <InsuranceLabsLayout
                data={insuranceLabs}
                setData={setInsuranceLabs}
                isExpanded={insuranceLabsExpanded}
                reasons={reasons}
                error={insurnaceLabsError}
                description={Resource.TASK_DESCRIPTION_INSURANCE_LABS}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_MIB_CODE_DETAILS}
              key="TASK_TITLE_MIB_CODE_DETAILS"
              labelClassName={styles.EvidenceTitle}
              renderToggle={toggleChild}
            >
              <MibCodeDetailsLayout
                data={mibCodeDetails}
                setData={setMibCodeDetails}
                isExpanded={mibCodeDetailsExpanded}
                reasons={reasons}
                error={mibCodeDetailsError}
                description={Resource.TASK_DESCRIPTION_MIB_CODE_DETAILS}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_CRIMINAL_RECORDS}
              key="TASK_TITLE_CRIMINAL_RECORDS"
              labelClassName={
                !taskTypeExist(TaskTypes.CRIMINAL_RECORDS) &&
                (productConfig?.automatedCriminalRecordsAvailable() ||
                  canOrderCriminalRecordsAsAdmin)
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={criminalRecords}
                setData={setCriminalRecords}
                isExpanded={criminalRecordsExpanded}
                reasons={reasons}
                error={criminalRecordsError}
                description={Resource.TASK_DESCRIPTION_CRIMINAL_RECORDS}
              />
            </AccordionSection>
            <AccordionSection
              title={Resource.TASK_TITLE_ADDITIONAL_CUSTOMER_INFO}
              key="TASK_TITLE_ADDITIONAL_CUSTOMER_INFO"
              labelClassName={
                canOrderInfoFromCustomerAccess
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={additionalCustomerInfo}
                setData={setAdditionalCustomerInfo}
                isExpanded={additionalCustomerInfoExpanded}
                error={addtionalCustomerError}
                description={Resource.TASK_DESCRIPTION_ADDITIONAL_CUSTOMER_INFO}
              />
            </AccordionSection>
            <AccordionSection
              title={Resource.TASK_TITLE_SPECIAL_AUTHORIZATION}
              key="TASK_TITLE_SPECIAL_AUTHORIZATION"
              labelClassName={
                flags.specialAuthEnabled && canCreateSpecialAuthorizationAccess
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={specialAuthorization}
                setData={setSpecialAuthorization}
                isExpanded={specialAuthorizationExpanded}
                error={specialAuthorizationError}
                description={Resource.TASK_DESCRIPTION_SPECIAL_AUTHORIZATION}
              />
            </AccordionSection>
            <AccordionSection
              title={Resource.TASK_TITLE_PLAN_F}
              key="TASK_TITLE_PLAN_F"
              labelClassName={
                flags.planFEnabled && canCreatePlanFAccess
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={planF}
                setData={setPlanF}
                isExpanded={planFExpanded}
                error={planFError}
                description={Resource.TASK_DESCRIPTION_PLAN_F}
              />
            </AccordionSection>

            <AccordionSection
              title={Resource.TASK_TITLE_DISPUTE}
              key="TASK_TITLE_DISPUTE"
              labelClassName={styles.EvidenceTitle}
              renderToggle={toggleChild}
            >
              <DisputeLayout
                dispute={dispute}
                setDispute={setDispute}
                isExpanded={disputeExpanded}
                disputeError={disputeError}
              />
            </AccordionSection>
            <AccordionSection
              title={Resource.TASK_TITLE_ADDITIONAL_DOCUMENTS}
              key="TASK_TITLE_ADDITIONAL_DOCUMENTS"
              labelClassName={
                canOrderAdditionalDocumentsAccess
                  ? styles.EvidenceTitle
                  : styles.Hide
              }
              renderToggle={toggleChild}
            >
              <SimpleLayout
                data={additionalDocuments}
                setData={setAdditionalDocuments}
                isExpanded={additionalDocumentsExpanded}
                error={additionalDocumentsError}
                description={Resource.TASK_DESCRIPTION_ADDITIONAL_DOCUMENTS}
              />
            </AccordionSection>
          </Accordion>
        </Box>
        <Box>
          <NoraButton
            name={REVIEW_BUTTON_NAME}
            className={styles.DrawerButton}
            onClick={() => {
              setMedExamError(!medExamValid);
              setEtiError(!etiValid);
              setEhrError(!ehrValid);
              setApsError(!apsValid);
              setLabPiqtureError(!labPiqtureValid);
              setAutoMedicalClaimsError(!autoMedicalClaimsValid);
              setTRLError(!trlValid);
              setMedicalClaimsError(!medicalClaimsValid);
              setScriptCheckError(!scriptCheckValid);
              setCriminalRecordsError(!criminalRecordsValid);
              setMibCodeDetailsError(!mibCodeDetailsValid);
              setInsurnaceLabsError(!insuranceLabsValid);
              setDisputeError(!disputeValid);
              setAdditionalDocumentsError(!additionalDocumentsValid);

              if (
                medExamValid &&
                etiValid &&
                ehrValid &&
                apsValid &&
                labPiqtureValid &&
                autoMedicalClaimsValid &&
                trlValid &&
                medicalClaimsValid &&
                scriptCheckValid &&
                criminalRecordsValid &&
                mibCodeDetailsValid &&
                insuranceLabsValid &&
                additionalCustomerValid &&
                specialAuthorizationValid &&
                planFValid &&
                disputeValid &&
                additionalDocumentsValid
              ) {
                setIsReview(true);
              }
            }}
          >
            Review Order
          </NoraButton>
        </Box>
      </Box>
    </Drawer>
  );
};
